import { useState } from 'react';
import { PackageOpen } from 'lucide-react';
import type { ActualsResponse, KpiCatalogueEntry, OpenEvidence } from '../../types/strategicKpis';
import { CATEGORY_LABEL } from '../../utils/strategicKpiFormat';
import { steeredFamilies, topGroup } from '../../utils/strategicKpiSelectors';
import { ContextKpis } from './ContextKpis';
import { SteeredKpiRows } from './SteeredKpiRows';
import { chipClass } from './parts';

interface Props {
    data: ActualsResponse;
    /** `steeredKpis(data)`: the KPIs the top node has a target for. */
    steered: KpiCatalogueEntry[];
    /** `contextKpis(data)`: Success Rate and Avg Days without a target. */
    context: KpiCatalogueEntry[];
    /** Opens the evidence of a number (a row, a context KPI) — the page draws it in the side panel (E9). */
    onOpenEvidence?: OpenEvidence;
    /** The KPI whose evidence the panel shows: its row is marked. */
    selectedKpiKey?: string | null;
    /** A refetch is running: the tab dims, it does not disappear. */
    loading?: boolean;
}

/**
 * The Dashboard tab (masterplan skpi-redesign B1; E4, E8, E12). From the top: the family pills, the strip of the
 * context KPIs (collapsible, one line when collapsed), and as the last block the one table of the tab — the card
 * «Steered KPIs» with «n of m behind plan» and the year progress in its head. The hero, the attention list, the
 * data hint, «Who is where» and the selection notice are gone (their content moved to the chip row, People and Data
 * quality). The page wires this in package B4: it passes the data, the two KPI lists and one `onOpenEvidence`.
 *
 * The pills filter the table's rows only — «n of m» always counts every steered KPI, as the hero did. The pill is
 * component state (it is not an address); it falls back to «All families» when the selection leaves that family
 * without a steered KPI.
 */
export function DashboardTab({ data, steered, context, onOpenEvidence, selectedKpiKey, loading = false }: Props) {
    const [picked, setPicked] = useState<string>('all');
    const top = topGroup(data);
    if (!top) {
        return (
            <div className="empty">
                <span className="ic"><PackageOpen className="i" aria-hidden="true" /></span>
                <b>No figures for this selection</b>
                <p>No group row for {data.bus.join(', ')} in {data.year} — nobody is registered for this year.</p>
            </div>
        );
    }
    const families = steeredFamilies(steered);
    const family = picked !== 'all' && families.includes(picked) ? picked : 'all';

    return (
        <div className={`flex flex-col gap-4${loading ? ' opacity-60' : ''}`}>
            {steered.length > 0 && (
                <div className="famrow" role="group" aria-label="KPI family">
                    <button type="button" className={chipClass(family === 'all')} aria-pressed={family === 'all'} onClick={() => setPicked('all')}>
                        All families <small>{steered.length}</small>
                    </button>
                    {families.map(f => (
                        <button key={f} type="button" className={chipClass(family === f)} aria-pressed={family === f} onClick={() => setPicked(f)}>
                            {CATEGORY_LABEL[f] ?? f} <small>{steered.filter(k => k.category === f).length}</small>
                        </button>
                    ))}
                </div>
            )}

            <ContextKpis data={data} context={context} onOpenEvidence={onOpenEvidence} />

            {steered.length > 0
                ? <SteeredKpiRows data={data} steered={steered} family={family} onOpenEvidence={onOpenEvidence} selectedKpiKey={selectedKpiKey} />
                : (
                    <div className="empty" data-testid="steered-rows">
                        <span className="ic"><PackageOpen className="i" aria-hidden="true" /></span>
                        <b>No KPI has a target for {data.year} at this level</b>
                        <p>Store targets on the Targets tab. Until then every KPI's figures are on the People tab.</p>
                    </div>
                )}
        </div>
    );
}
